/**
 * Notifications Page
 *
 * Full page view for all user notifications
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
  useUnreadNotificationCount,
  useNotificationScope,
} from '@/hooks/useNotifications';
import { Notification, NotificationScope } from '@/types/notification';
import { useTimezone } from '@/contexts';
import { getNotificationHref, getNotificationVisual, translateNotification } from '@/utils/notificationUtils';
import { NotificationBrandChip } from '@/components/notifications/NotificationBrandChip';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { focusSurface } from '@/components/ui/focusStyles';

/**
 * Format relative date for notifications
 * Shows time for today, "Yesterday" for yesterday, weekday for < 7 days, else date
 */
const useFormattedNotificationDate = () => {
  const { formatTimeOnly, formatDateOnly, formatDate } = useTimezone();
  const { t } = useTranslation('common');

  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return formatTimeOnly(dateString);
    } else if (diffDays === 1) {
      return t('notifications_yesterday');
    } else if (diffDays < 7) {
      return formatDate(dateString, 'weekday');
    } else {
      return formatDateOnly(dateString);
    }
  };
};

/**
 * Notification Item Component
 */
interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  scope: NotificationScope;
}

const NotificationItem = ({ notification, onMarkAsRead, onDelete, scope }: NotificationItemProps) => {
  const formatNotificationDate = useFormattedNotificationDate();
  const { t } = useTranslation('common');
  const { icon, color } = getNotificationVisual(notification);

  // Translate notification
  const translated = translateNotification(
    notification.type,
    notification.title,
    notification.message,
    { teamName: notification.teamName ?? undefined, ...(notification.data as Record<string, unknown> | undefined) },
    t
  );

  return (
    <div
      className={`group relative flex items-start gap-4 p-4 rounded-lg transition-colors ${
        notification.isRead ? 'bg-white' : 'bg-soft-mint'
      } hover:bg-gray-50`}
    >
      {/* Icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <Icon name={icon} size="custom" color={color} className="text-[18px]" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <Link
          to={getNotificationHref(notification, scope)}
          onClick={() => !notification.isRead && onMarkAsRead(notification.id)}
          className="block"
        >
          <p className="flex items-start gap-2 text-sm font-medium text-gray-900">
            {!notification.isRead && (
              <span className="mt-1.5 h-2 w-2 aspect-square flex-none rounded-full bg-primary-600" aria-hidden="true" />
            )}
            <span>{translated.title}</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">{translated.body}</p>
          {scope === 'portfolio' && notification.teamName && (
            <div className="mt-2 flex">
              <NotificationBrandChip name={notification.teamName} logo={notification.teamLogo} />
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">{formatNotificationDate(notification.createdAt)}</p>
        </Link>
      </div>

      {/* Actions */}
      <div className="flex flex-none flex-col items-start gap-0 sm:flex-row sm:gap-0.5 opacity-100 transition-opacity sm:pointer-events-none sm:opacity-0 sm:group-hover:pointer-events-auto sm:group-hover:opacity-100 sm:group-focus-within:pointer-events-auto sm:group-focus-within:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100">
        {!notification.isRead && (
          <Tooltip content={t('notifications_mark_as_read')}>
            <button
              type="button"
              onClick={() => onMarkAsRead(notification.id)}
              aria-label={t('notifications_mark_as_read')}
              className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-transparent text-gray-700 hover:bg-gray-100 hover:text-primary-700 ${focusSurface}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </Tooltip>
        )}
        <Tooltip content={t('notifications_delete')}>
          <button
            type="button"
            onClick={() => onDelete(notification.id)}
            aria-label={t('notifications_delete')}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-transparent text-gray-700 hover:bg-gray-100 hover:text-red-700 ${focusSurface}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

/**
 * Notifications Page Component
 */
export const NotificationsPage = () => {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const { t } = useTranslation('common');

  // Queries
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteNotifications(20, filter === 'unread');

  const scope = useNotificationScope();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  // Mutations
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteMutation = useDeleteNotification();

  // Flatten pages into single array
  const notifications = data?.pages.flatMap((page) => page.notifications) || [];

  // Filter options for Select component
  const filterOptions = [
    { value: 'all', label: t('notifications_filter_all') },
    { value: 'unread', label: t('notifications_filter_unread_only') },
  ];

  const handleMarkAsRead = (id: string) => {
    markAsReadMutation.mutate(id);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  return (
    <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('notifications_title')}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {unreadCount > 0 ? t('notifications_unread', { count: unreadCount }) : t('notifications_all_caught_up')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            {/* Filter */}
            <Select
              value={filter}
              onValueChange={(value) => setFilter(value as 'all' | 'unread')}
              options={filterOptions}
              size="sm"
              className="max-sm:min-h-[44px]"
            />

            {/* Mark all as read */}
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={markAllAsReadMutation.isPending}
                className={`inline-flex min-h-[44px] items-center whitespace-nowrap rounded-lg px-3 text-sm font-medium text-primary-700 hover:bg-gray-100 hover:text-primary-800 disabled:opacity-50 ${focusSurface}`}
              >
                {t('notifications_mark_all_as_read')}
              </button>
            )}

            {/* Settings link */}
            <Tooltip content={t('notifications_settings')}>
              <Link
                to="/settings?tab=notifications"
                aria-label={t('notifications_settings')}
                className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 ${focusSurface}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>
            </Tooltip>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-gray-500">{t('notifications_loading')}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <svg
                className="w-16 h-16 mx-auto text-gray-300 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <p className="text-gray-500 text-lg">{t('notifications_empty_title')}</p>
              <p className="text-gray-400 text-sm mt-1">
                {filter === 'unread' ? t('notifications_empty_unread') : t('notifications_empty_all')}
              </p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  scope={scope}
                />
              ))}

              {/* Load More */}
              {hasNextPage && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {isFetchingNextPage ? t('notifications_loading_more') : t('notifications_load_more')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  );
};

export default NotificationsPage;
